import classNames from 'classnames'
import { downloadZip } from 'client-zip'
import { saveAs } from 'file-saver'
import { useFormikContext } from 'formik'
import { useEffect } from 'react'
import { MdBuild, MdKeyboardArrowLeft, MdKeyboardArrowRight, MdSave, MdTransform } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { IPageContainer, PageContainerParamId } from '~/types/page-container'
import type { IPageContainerFormValues, ITemplateConfigOptions } from '~/types/template'

import ActionMenu from '~/components/ActionMenu'
import Button from '~/components/Buttons/Button'
import LinkButton from '~/components/Buttons/LinkButton'
import InfiniteProgressBar from '~/components/InfiniteProgressBar'
import { AUTHORIZATIONS } from '~/const'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { PAGE_CONTAINER_STATUS } from '~/types/page-container'
import { isFormCompleted } from '~/utils/page-container'

import './styles.scss'

interface Props {
  isLoading: boolean
  onCompile: VoidFunction
  onGenerateScreenshots: () => Promise<void>
  onProcess: VoidFunction
  onSelectorClick: (index: number) => void
  onTranslationErrorsClick?: VoidFunction
  onUploadScreenshots: VoidFunction
  pageContainer?: IPageContainer
  pageIndex: number
  template: ITemplateConfigOptions
}

const PageSelector = ({
  isLoading,
  onCompile,
  onGenerateScreenshots,
  onProcess,
  onSelectorClick,
  onTranslationErrorsClick,
  onUploadScreenshots,
  pageContainer,
  pageIndex,
  template,
}: Props): ReactElement => {
  const navigate = useNavigate()

  const { setFieldValue, values } = useFormikContext<IPageContainerFormValues>()

  const { pageContainerId } = useParams<{ pageContainerId: PageContainerParamId }>()

  const isEdition = pageContainerId && !['creation', 'duplication'].includes(pageContainerId)

  const isCompilable = pageContainer?.status === PAGE_CONTAINER_STATUS.PREPARED

  const isUserAuthorized = useIsUserAuthorized([AUTHORIZATIONS.DYNAMIC_SCREENS.PAGE_CONTAINER.WRITE])

  const minIndex = 0

  const maxIndex = template.config.length - 1

  const isEditable = pageContainer?.status === PAGE_CONTAINER_STATUS.DRAFT || !pageContainer

  const handlePageButtonClick = (index: number) => {
    onSelectorClick(index)
  }

  const handleNext = () => {
    onSelectorClick(pageIndex + 1)
  }

  const handleBack = () => {
    onSelectorClick(pageIndex - 1)
  }

  const handleDownloadScreenshots = async () => {
    if (pageContainer?.screenshot_file_urls) {
      const urls = pageContainer?.screenshot_file_urls

      // @ts-expect-error bad types
      const zipContent = await downloadZip(urls.map((url) => fetch(url))).blob()
      saveAs(zipContent, `${pageContainer?.title}.zip`)
    }
  }

  const handleCreateFrom = () =>
    navigate('/dynamic-screens/page-containers/duplication', {
      state: { pageContainer },
    })

  const actionMenu: Menu = {
    actions: [
      {
        action: onGenerateScreenshots,
        label: 'Generate screenshots',
      },
      {
        action: onUploadScreenshots,
        label: 'Upload screenshots',
      },
    ],
  }

  if (pageContainer?.screenshot_file_urls && pageContainer.screenshot_file_urls.length > 0) {
    actionMenu.actions?.push({
      action: handleDownloadScreenshots,
      label: 'Download screenshots',
    })
  }

  if (pageContainer?.status !== PAGE_CONTAINER_STATUS.COMPILED) {
    actionMenu.actions?.push({
      action: handleCreateFrom,
      label: 'Create from',
    })
  }

  useEffect(() => {
    const filters = template.config[pageIndex].filters
    if (filters?.length) {
      setFieldValue('templateFilter', filters[0])
    }
  }, [pageIndex, setFieldValue, template.config])

  return (
    <div className="page-selector-component">
      {isLoading ? <InfiniteProgressBar /> : null}
      <div className="empty-div" />
      <div className="stepper">
        {template.config.length > 1 && (
          <Button className="command-button" disabled={pageIndex === minIndex} ghost onClick={handleBack} type="button">
            <MdKeyboardArrowLeft color="#62625d" size={15} />
          </Button>
        )}
        {template.config.map(({ name }, index) => (
          <div
            className={classNames('page-selector', { active: pageIndex === index })}
            key={`page-button-${index}`}
            onClick={() => handlePageButtonClick(index)}
          >
            {name}
          </div>
        ))}
        {template.config.length > 1 && (
          <Button className="command-button" disabled={pageIndex === maxIndex} ghost onClick={handleNext} type="button">
            <MdKeyboardArrowRight color="#62625D" size={15} />
          </Button>
        )}
      </div>
      <div className="action-buttons">
        {onTranslationErrorsClick ? (
          <LinkButton
            backgroundColor="light"
            className="errors-link-button"
            label="Show errors"
            onClick={onTranslationErrorsClick}
          />
        ) : null}
        {isEditable && isUserAuthorized && (
          <Button disabled={isLoading} type="submit">
            <MdSave /> Save
          </Button>
        )}
        {isEditable &&
          isUserAuthorized &&
          isFormCompleted(values.placeholders) &&
          isEdition &&
          values.title !== 'Enter a title' && (
            <Button disabled={isLoading} onClick={onProcess} type="button">
              <MdBuild /> Process & compile
            </Button>
          )}
        {isCompilable && isUserAuthorized && isEdition && (
          <Button disabled={isLoading} onClick={onCompile} type="button">
            <MdTransform /> Compile
          </Button>
        )}
        {pageContainer?.status === PAGE_CONTAINER_STATUS.COMPILED ? (
          <Button onClick={handleCreateFrom} outlined type="button">
            Create from
          </Button>
        ) : null}
        {isEdition && <ActionMenu iconDirection="vertical" menu={actionMenu} placement="top" />}
      </div>
    </div>
  )
}

export default PageSelector
